import { Button } from "@cynthai/ui";
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
} from "@tanstack/react-router";
import z from "zod";

import { getDatasourceFileUrl } from "@/core/lib/api";
import {
  useCurrentProject,
  resolveAuditActive,
} from "@/features/project/components/pages/project-page-context-hooks";
import {
  getProjectsOptions,
  getDocumentAnalysisTemplateOptions,
  getProjectHierarchyOptions,
  listProjectVectorizedDatasourcesOptions,
} from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

const searchSchema = z.object({
  datasourceId: z.uuid(),
  templateId: z.uuid(),
});

function AnalysisRunNotFound() {
  const client = useCurrentProject();

  return (
    <div className="flex h-full min-h-[80vh] items-center justify-center p-6">
      <div className="bg-background-2 border-border w-full max-w-xl rounded-2xl border p-8 text-center shadow-sm">
        <p className="text-primary text-xs font-semibold tracking-[0.25em] uppercase">
          {m.answer_not_found()}
        </p>
        <h1 className="text-text-primary mt-3 text-2xl font-semibold">
          {m.document_analysis_not_found()}
        </h1>
        <p className="text-text-secondary mt-3 text-sm leading-6">
          {m.document_analysis_not_found_description()}
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/auditor/clients/$id/document-analysis"
            params={{ id: client.id }}
          >
            <Button
              className="flex items-center gap-2"
              color="primary"
              variant="default"
            >
              {m.back_to_document_analysis()}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_protected/auditor/clients/$id/analysis/$rid",
)({
  validateSearch: searchSchema,
  notFoundComponent: AnalysisRunNotFound,
  loaderDeps: ({ search: { datasourceId, templateId, entityProjectId } }) => ({
    datasourceId,
    templateId,
    entityProjectId,
  }),
  loader: async ({ context: { queryClient, ...context }, deps, params }) => {
    const orgId = context.user.organization?.id;

    if (!orgId) {
      throw redirect({ to: "/no-organization", throw: true });
    }

    // resolve the active dossier: on canonical urls params.id is the client
    // root (cache hit: the parent route ensured the hierarchy)
    const hierarchy = await queryClient.query({
      ...getProjectHierarchyOptions({
        path: { org_id: orgId, project_id: params.id },
      }),
      staleTime: Infinity,
    });
    const activeProjectId = resolveAuditActive(
      hierarchy.root,
      hierarchy.children,
      deps.entityProjectId,
    ).id;

    const [auditDatasourcesData, template, clientsData] = await Promise.all([
      queryClient.query(
        listProjectVectorizedDatasourcesOptions({
          path: { org_id: orgId, project_id: activeProjectId },
        }),
      ),
      // Fetch specific template by ID - this returns archived templates too,
      // which is needed for viewing historical analysis runs
      queryClient.query(
        getDocumentAnalysisTemplateOptions({
          path: { org_id: orgId, template_id: deps.templateId },
        }),
      ),
      queryClient.query({
        ...getProjectsOptions({ path: { org_id: orgId } }),
        staleTime: Infinity,
      }),
    ]);

    const auditDatasource = auditDatasourcesData.find(
      (d) => d.datasource?.id === deps.datasourceId,
    );

    if (!auditDatasource?.datasource) {
      throw notFound();
    }

    const pdfUrl = getDatasourceFileUrl(auditDatasource.datasource);
    if (!pdfUrl) {
      throw notFound();
    }

    const client = clientsData.find((c) => c.id === activeProjectId);

    if (!client) {
      throw notFound();
    }

    return {
      datasource: auditDatasource.datasource,
      template,
      client: client,
      pdfUrl,
      runNumber: Number.parseInt(params.rid),
    };
  },
});
